// Utility functions for getting report properties (colors, titles, etc.)
// Adapted from the mobile app's getReportProps.tsx

import i18n from "i18next";
import { ReportData } from "../types/global.d";

// Status colors — aligned to the "Cartographer" design tokens (styles/tokens.css).
export const COLORS = {
  mainDarkerColor: "#7DA62F", // Green  — fully completed (both verified)  (--c-success)
  errorColor: "#E5564B", // Red    — verified but not cleaned             (--c-danger)
  warnColor: "#E8920C", // Amber  — verified report, unverified cleaning  (--c-warning)
  greyColor: "#8A8E7E", // Muted  — unverified reports                    (--c-ink-muted)
};

// Numeric report-status enum (server source of truth, present in every tier
// including the status-only minimal pins that carry no verified/cleaned flags).
export const REPORT_STATUS = {
  REPORT_ONLY: 1, // not verified, no cleaning            -> grey
  REPORT_VERIFIED: 2, // verified, not cleaned             -> red
  REPORT_WITH_CLEANING: 4, // has cleaning, unverified      -> amber
  VERIFIED_REPORT_WITH_CLEANING: 5, // report verified, cleanup not -> amber
  CLEANING_VERIFIED_ONLY: 7, // cleaning verified            -> green
  FULLY_COMPLETED: 8, // both verified                     -> green
} as const;

/**
 * Map the numeric report status to a marker color. This is the source of truth
 * for minimal-tier pins, which arrive with only `status` (no verified/cleaned).
 * Returns null when the status is not a recognised numeric code so callers can
 * fall back to the flag-based logic.
 */
export const getReportColorByStatus = (
  status: ReportData["status"]
): string | null => {
  const numericStatus = Number(status);
  if (!Number.isFinite(numericStatus)) {
    return null;
  }

  switch (numericStatus) {
    case REPORT_STATUS.REPORT_VERIFIED:
      return COLORS.errorColor;
    case REPORT_STATUS.REPORT_WITH_CLEANING:
    case REPORT_STATUS.VERIFIED_REPORT_WITH_CLEANING:
      return COLORS.warnColor;
    case REPORT_STATUS.CLEANING_VERIFIED_ONLY:
    case REPORT_STATUS.FULLY_COMPLETED:
      return COLORS.mainDarkerColor;
    case REPORT_STATUS.REPORT_ONLY:
      return COLORS.greyColor;
    default:
      return null;
  }
};

/**
 * Get the color for a report marker based on its status.
 * Prefers the numeric `status` (works for every tier and matches the mobile
 * app's cluster coloring, so a pin keeps its color when it upgrades from
 * minimal to full). Falls back to the verified/cleaned flags for any report
 * that lacks a recognised numeric status.
 * @param report - The report data
 * @returns Color string for the marker
 */
export const getReportColor = (report: ReportData): string => {
  const statusColor = getReportColorByStatus(report.status);
  if (statusColor) {
    return statusColor;
  }

  // Report not verified - grey
  if (!report.verified) {
    return COLORS.greyColor;
  }

  // Report verified and cleaned
  if (report.cleaned) {
    // Both verified - green (fully completed)
    if (report.cleaned.verified) {
      return COLORS.mainDarkerColor;
    }
    // Report verified, cleanup not verified - orange
    return COLORS.warnColor;
  }

  // Report verified but not cleaned - red
  return COLORS.errorColor;
};

/**
 * Resolve the title i18n key from the numeric status (works for minimal-tier
 * pins that have no verified/cleaned flags). Returns null for unknown statuses.
 */
const getReportTitleKeyByStatus = (
  status: ReportData["status"]
): string | null => {
  const numericStatus = Number(status);
  if (!Number.isFinite(numericStatus)) {
    return null;
  }
  switch (numericStatus) {
    case REPORT_STATUS.CLEANING_VERIFIED_ONLY:
    case REPORT_STATUS.FULLY_COMPLETED:
      return "report.verifiedCleanup";
    case REPORT_STATUS.REPORT_WITH_CLEANING:
    case REPORT_STATUS.VERIFIED_REPORT_WITH_CLEANING:
      return "report.cleanupVerification";
    case REPORT_STATUS.REPORT_VERIFIED:
      return "report.pollutionReport";
    case REPORT_STATUS.REPORT_ONLY:
      return "report.pendingVerification";
    default:
      return null;
  }
};

/**
 * Get the title/status text for a report.
 * Prefers the numeric `status` (works for every tier) and falls back to the
 * verified/cleaned flags.
 * @param report - The report data
 * @returns Status string for the report
 */
export const getReportTitle = (report: ReportData): string => {
  const statusKey = getReportTitleKeyByStatus(report.status);
  if (statusKey) {
    return (i18n.t as any)(statusKey);
  }
  if (report.cleaned) {
    return report.cleaned.verified
      ? (i18n.t as any)("report.verifiedCleanup")
      : (i18n.t as any)("report.cleanupVerification");
  }
  if (!report.verified) {
    return (i18n.t as any)("report.pendingVerification");
  }
  return (i18n.t as any)("report.pollutionReport");
};

/**
 * Get the appropriate image URL for a report
 * @param report - The report data
 * @param isAdmin - Whether the current user is an admin
 * @param isOwner - Whether the current user owns this report
 * @returns Image URL or null if not available
 */
export const getReportImageUrl = (
  report: ReportData,
  isAdmin: boolean = false,
  isOwner: boolean = false
): string | null => {
  // For unverified reports, only show images to admin or owner
  if (!report.verified && !isAdmin && !isOwner) {
    return null;
  }

  // If it's cleaned, show cleanup image
  if (report.cleaned && report.cleaned.images.length > 0) {
    // For unverified cleanups, only show to admin or owner
    if (!report.cleaned.verified && !isAdmin && !isOwner) {
      return null;
    }
    return report.cleaned.images[0];
  }

  // Show original report image
  if (report.images && report.images.length > 0) {
    return report.images[0];
  }

  return null;
};

/**
 * Get a formatted date string for display
 * @param report - The report data
 * @returns Formatted date string
 */
export const getReportDate = (report: ReportData): string => {
  const date = report.cleaned?.date || report.date;
  if (!date) return "No date";

  // If the date is already formatted nicely, return as is
  // Otherwise, you might want to add date formatting logic here
  return date;
};

/**
 * Get status information for a report
 * @param report - The report data
 * @returns Object with status info
 */
export const getReportStatus = (report: ReportData) => {
  return {
    color: getReportColor(report),
    title: getReportTitle(report),
    date: getReportDate(report),
    imageUrl: getReportImageUrl(report),
    isVerified: report.verified,
    isCleaned: !!report.cleaned,
    isCleanupVerified: report.cleaned?.verified || false,
  };
};
